import React from 'react';
import { parseSupportMessageCsat } from '../../../utils/supportMessageCsat';

interface WorkspaceCsatCardProps {
  content?: string | null;
  contentAttributes?: string | null;
}

interface CsatState {
  label: string;
  badgeColor: string;
  icon: string;
}

function resolveCsatState(submittedRating?: number | null, openedAt?: string | null): CsatState {
  if (submittedRating)
  {
    return {
      label: 'Respondido',
      badgeColor: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      icon: '✓',
    };
  }

  if (openedAt)
  {
    return {
      label: 'Visualizado',
      badgeColor: 'bg-blue-100 text-blue-700 border-blue-200',
      icon: '👁',
    };
  }

  return {
    label: 'Enviado',
    badgeColor: 'bg-gray-100 text-gray-700 border-gray-200',
    icon: '📤',
  };
}

function getRatingInfo(rating: number): { emoji: string; label: string; color: string } {
  const ratingMap: Record<number, { emoji: string; label: string; color: string }> = {
    1: { emoji: '😠', label: 'Muito insatisfeito', color: 'text-red-600' },
    2: { emoji: '😕', label: 'Insatisfeito', color: 'text-orange-600' },
    3: { emoji: '😐', label: 'Neutro', color: 'text-yellow-600' },
    4: { emoji: '🙂', label: 'Satisfeito', color: 'text-lime-600' },
    5: { emoji: '😄', label: 'Muito satisfeito', color: 'text-green-600' },
  };

  return ratingMap[rating] ?? { emoji: String(rating), label: '', color: 'text-gray-600' };
}

export const WorkspaceCsatCard: React.FC<WorkspaceCsatCardProps> = ({
  content,
  contentAttributes,
}) => {
  const csat = parseSupportMessageCsat(contentAttributes);

  if (!csat.isCsat)
  {
    return null;
  }

  const state = resolveCsatState(csat.submittedRating, csat.openedAt);
  const ratingInfo = csat.submittedRating ? getRatingInfo(csat.submittedRating) : null;

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm hover:shadow-md transition-all duration-200">
      {/* Header */}
      <div className="px-4 py-3 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-indigo-100">
              <span className="text-lg" role="img" aria-label="Pesquisa">💬</span>
            </div>
            <div className="font-medium text-gray-900 text-sm">
              {content || 'Pesquisa de Satisfação'}
            </div>
          </div>
          <span className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium border ${state.badgeColor}`}>
            <span className="text-sm leading-none">{state.icon}</span>
            {state.label}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-4">
        {csat.submittedRating && ratingInfo ? (
          <div className="space-y-4">
            {/* Rating Display */}
            <div>
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
                Avaliação recebida
              </div>
              <div className="flex items-start gap-4">
                {csat.displayType === 'star' ? (
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map(i => (
                        <svg
                          key={i}
                          className={`w-7 h-7 ${i <= csat.submittedRating! ? 'text-yellow-400' : 'text-gray-300'}`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                    <div>
                      <div className={`text-base font-semibold ${ratingInfo.color}`}>
                        {ratingInfo.label}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        Nota {csat.submittedRating} de 5
                      </div>
                    </div>
                  </div>
                ) : csat.displayType === 'emoji' ? (
                  <>
                    <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 shrink-0">
                      <span className="text-4xl" role="img" aria-label="Avaliação">
                        {ratingInfo.emoji}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className={`text-lg font-semibold ${ratingInfo.color}`}>
                        {ratingInfo.label}
                      </div>
                      <div className="text-sm text-gray-500 mt-0.5">
                        Nota {csat.submittedRating} de 5
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200">
                      <span className="text-3xl font-bold text-gray-700">
                        {csat.submittedRating}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className={`text-lg font-semibold ${ratingInfo.color}`}>
                        {ratingInfo.label}
                      </div>
                      <div className="text-sm text-gray-500 mt-0.5">
                        Nota {csat.submittedRating} de 5
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Feedback */}
            {csat.submittedFeedback && (
              <div>
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                  Comentário
                </div>
                <div className="rounded-lg bg-gray-50 px-3.5 py-3 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap border border-gray-200">
                  {csat.submittedFeedback}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 shrink-0 mt-0.5">
              <span className="text-xl" role="img" aria-label="Status">
                {csat.openedAt ? '⏳' : '📨'}
              </span>
            </div>
            <div className="flex-1 pt-1">
              <p className="text-sm text-gray-700 leading-relaxed">
                {csat.openedAt ? (
                  <>
                    <span className="font-medium text-gray-900">Cliente visualizou a pesquisa</span>
                    <br />
                    <span className="text-gray-600">Aguardando resposta...</span>
                  </>
                ) : (
                  <>
                    <span className="font-medium text-gray-900">Pesquisa enviada ao cliente</span>
                    <br />
                    <span className="text-gray-600">
                      Aguardando visualização e resposta{csat.askComment ? ' (comentário opcional)' : ''}.
                    </span>
                  </>
                )}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
